/**
 * Names that survive a student having no surname.
 *
 * First-name-only rosters import now — primary, ESL, tutoring, a team sheet —
 * so `lastName` is legitimately `''`, and two patterns spread across the app
 * quietly assume otherwise:
 *
 *   `${lastName}, ${firstName}`   → ", Amelia" on printed registers
 *   `a.lastName.localeCompare(b.lastName)` → every comparison returns 0, so the
 *                                            list is insertion order and a
 *                                            "sort by last name" header does
 *                                            nothing at all.
 *
 * Both live here so the next surname-less roster meets one implementation
 * rather than a dozen.
 */

export interface NameLike {
  firstName?: string;
  lastName?: string;
}

const t = (v?: string) => (v || '').trim();

/** "Nguyen, Amelia" — or just "Amelia" when there is no surname to put first. */
export function lastFirstName(s: NameLike): string {
  const last = t(s.lastName);
  const first = t(s.firstName);
  if (!last) return first;
  if (!first) return last;
  return `${last}, ${first}`;
}

/**
 * The key an alphabetical list should sort on.
 *
 * Surname first, then first name — and the leading `trim()` is what makes a
 * surname-less student work: the key collapses to their first name, so they
 * file under it rather than comparing '' with '' and never moving. A mixed
 * class therefore interleaves, which is what a teacher scanning the list
 * expects.
 *
 * (An explicit `lastName || firstName` fallback was here first. It was
 * unobservable — trim already produced the identical key — so it went, rather
 * than sit in the code implying it did something.)
 */
export function studentSortKey(s: NameLike): string {
  return `${t(s.lastName)} ${t(s.firstName)}`.trim().toLowerCase();
}

/** Comparator for the above. */
export const byStudentName = (a: NameLike, b: NameLike): number =>
  studentSortKey(a).localeCompare(studentSortKey(b), 'en');
